import { FC, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Tag } from '@factorialco/deprecated-design-system'
import {
  TasksValueObjectsTaskIdentifierEnum,
  TasksValueObjectsTaskStatusEnum,
  UpdateTaskDocument,
} from 'generated/resources/graphql'
import { DocumentNode } from 'graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { RefetchQuery } from 'modules/tasks/hooks/useBulkUpdateTasks'
import { statusOption, taskStatuses } from 'modules/tasks/lib/status'

interface Props {
  taskId: number
  status?: TasksValueObjectsTaskStatusEnum | null
  identifier: TasksValueObjectsTaskIdentifierEnum
  refetchQueries?: DocumentNode[] | RefetchQuery[]
}

const TaskStatus: FC<Props> = ({ taskId, status, identifier, refetchQueries }) => {
  const { pathname } = useLocation()
  const [loading, setLoading] = useState(false)

  const [updateTask] = useMutation(UpdateTaskDocument, {
    extractorFn: (data) => data.tasksMutations.updateTasksTask,
  })
  const [selectedStatus, setSelectedStatus] = useState<
    TasksValueObjectsTaskStatusEnum | undefined | null
  >(status)

  const pathPage = () => {
    if (pathname.includes('my-projects')) return 'my projects employee view'
    if (pathname.includes('contributors')) return 'my projects manager view'
    return 'project tasks'
  }

  const trackEvent = () => {
    if (identifier === 'project') {
      track(trackingEvents.projectManagement.tasks.updateTaskStatus, {
        mark_as: `Mark as ${status}`,
        number: 1,
        identifier: identifier,
        path_page: pathPage(),
        origin: 'single_update',
      })
    }
  }

  useEffect(() => {
    setSelectedStatus(status)
  }, [status]) // eslint-disable-line react-hooks/exhaustive-deps

  const onSelectorChange = async (value: TasksValueObjectsTaskStatusEnum) => {
    setSelectedStatus(value)
    setLoading(true)
    trackEvent()

    const result = await updateTask({
      variables: {
        id: taskId,
        status: value,
        identifier,
      },
      refetchQueries,
    })

    const handled = handleResult(result, (_) => {})
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
    }
    setLoading(false)
  }

  const selectedOption = statusOption(selectedStatus)

  return (
    <Box flexDirection="row">
      <PopoverMenu type="list">
        <PopoverMenu.Trigger disabled={loading}>
          <Tag showCircle type={selectedOption?.type} inline rightItem="dropdown" showActionIcon>
            {selectedOption?.title}
          </Tag>
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="start">
          {taskStatuses().map((option) => (
            <PopoverMenu.TouchableOption
              key={option.status}
              onClick={() => onSelectorChange(option.status)}
              title={option.title}
            />
          ))}
        </PopoverMenu.Content>
      </PopoverMenu>
    </Box>
  )
}

export default TaskStatus
